import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApartmentType,
  CarOwnerShipType,
  Debtor,
  DebtType,
  Education,
  EmployerIndustry,
  EmploymentStatus,
  ExpenseKind,
  JobPosition,
  loan_application,
  MaritalStatus,
  Residence,
  ResidencePermit,
} from '@axo/shared/data-access/types';
import { useMemo } from 'react';
import { FieldPath } from 'react-hook-form';
import { z } from 'zod';
import { debounceFunction } from '@axo/shared/util/fp';
import {
  loan_application_hooks,
  useLoanApplication,
} from '@axo/shared/data-access/hooks';
import { ApplicantMode } from '@axo/shared/data-access/types/loan_request';

export function useApplicationDataFormSchema(applicationID: string) {
  const { data: applicationData } = useLoanApplication(applicationID);
  const { mutateAsync: checkApplication } =
    loan_application_hooks.useCheckApplicationValidation();

  const debtItem = useMemo(
    () =>
      z.object({
        ID: z.string().uuid().nullable(),
        Type: z.nativeEnum(DebtType).nullable(),
        OutstandingAmount: z.number(),
        MonthlyDebtPayment: z.number().nullable(),
        RefinanceAmount: z.number().nullable(),
        Source: z.string().nullable(),
        Creditor: z.string().nullable(),
        Debtor: z.nativeEnum(Debtor).nullable(),
        NominalInterestRate: z.number().nullable(),
      }),
    [],
  );

  const mainApplicantSchema = useMemo(
    () =>
      z.object({
        MainBank: z.string().nullable(),
        Citizenship: z.string().nullable(),
        LivedInMarketCountrySince: z.string().nullable(),
        Education: z.nativeEnum(Education).nullable(),
        MilitaryServiceCompleted: z.boolean().nullable(),
        PoliticallyInfluential: z.boolean().nullable(),
        EmploymentStatus: z.nativeEnum(EmploymentStatus).nullable(),
        CompanyOrganizationNumber: z.string().nullable(),
        Employer: z.string().nullable(),
        EmployerIndustry: z.nativeEnum(EmployerIndustry).nullable(),
        EmploymentStatusSince: z.string().nullable(),
        EmploymentStatusUntil: z.string().nullable(),
        JobTitle: z.string().nullable(),
        JobPosition: z.nativeEnum(JobPosition).nullable(),
        GrossIncomeMonthly: z.number().nullable(),
        GrossIncomeYearly: z.number().nullable(),
        NetIncomeMonthly: z.number().nullable(),
        ExtraIncomeMonthly: z.number().nullable(),
        ExtraIncomeRental: z.number().nullable(),
        ExtraIncomeChildSupport: z.number().nullable(),
        LivingCostMonthly: z.number().nullable(),
        MaritalStatus: z.nativeEnum(MaritalStatus).nullable(),
        PartnerGrossIncomeMonthly: z.number().nullable(),
        PartnerGrossIncomeYearly: z.number().nullable(),
        PartnerNetIncomeYearly: z.number().nullable(),
        PartnerLivingCostMonthly: z.number().nullable(),
        HousingType: z.nativeEnum(Residence).nullable(),
        ApartmentType: z.nativeEnum(ApartmentType).nullable(),
        LivedAtAddressSince: z.string().nullable(),
        RentCostMonthly: z.number().nullable(),
        OwnerAssociationCostMonthly: z.number().nullable(),
        Debts: z.array(debtItem),
        Children: z.array(
          z.object({
            ID: z.string().uuid().nullable(),
            Age: z.number().nullable(),
          }),
        ),
        ResidencePermit: z.nativeEnum(ResidencePermit).nullable(),
        ResidencePermitType: z.string().nullable(),
        ResidencePermitNumber: z.string().nullable(),
        ResidencePermitExpiration: z.string().nullable(),
        HasUnemploymentFund: z.boolean().nullable(),
        YearsAsBankCustomer: z.number().nullable(),
        HasDankort: z.boolean().nullable(),
        HasOtherPaymentCards: z.boolean().nullable(),
        CurrentYearDebtOutstandingAmount: z.number().nullable(),
        OwnsHolidayHome: z.boolean().nullable(),
        HasCars: z.boolean().nullable(),
        CarOwnerShipType: z.nativeEnum(CarOwnerShipType).nullable(),
        CarTotalCostMonthly: z.number().nullable(),
        ExpensesPhoneInternetTV: z.number().nullable(),
        ExpensesTransport: z.number().nullable(),
        ExpensesElectricity: z.number().nullable(),
        ExpensesWater: z.number().nullable(),
        ExpensesHeating: z.number().nullable(),
        ExpensesInsurances: z.number().nullable(),
      }),
    [debtItem],
  );

  const coApplicantSchema = useMemo(
    () =>
      z.object({
        CoApplicantCitizenship: mainApplicantSchema.shape.Citizenship,
        CoApplicantLivedInMarketCountrySince:
          mainApplicantSchema.shape.LivedInMarketCountrySince,
        CoApplicantEducation: mainApplicantSchema.shape.Education,
        CoApplicantEmployer: mainApplicantSchema.shape.Employer,
        CoApplicantEmploymentStatus: mainApplicantSchema.shape.EmploymentStatus,
        CoApplicantCompanyOrganizationNumber:
          mainApplicantSchema.shape.CompanyOrganizationNumber,
        CoApplicantEmploymentStatusSince:
          mainApplicantSchema.shape.EmploymentStatusSince,
        CoApplicantEmploymentStatusUntil:
          mainApplicantSchema.shape.EmploymentStatusUntil,
        CoApplicantGrossIncomeYearly:
          mainApplicantSchema.shape.GrossIncomeYearly,
        CoApplicantNetIncomeYearly: z.number().nullable(),
        CoApplicantLivingCostMonthly:
          mainApplicantSchema.shape.LivingCostMonthly,
        CoApplicantMaritalStatus: mainApplicantSchema.shape.MaritalStatus,
        CoApplicantPartnerGrossIncomeYearly:
          mainApplicantSchema.shape.PartnerGrossIncomeYearly,
        CoApplicantIsCohabitant: z.boolean().nullable(),
        CoApplicantHousingType: mainApplicantSchema.shape.HousingType,
        CoApplicantLivedAtAddressSince:
          mainApplicantSchema.shape.LivedAtAddressSince,
        CoApplicantRentCostMonthly: mainApplicantSchema.shape.RentCostMonthly,
        CoDebts: mainApplicantSchema.shape.Debts,
        CoApplicantResidencePermit: mainApplicantSchema.shape.ResidencePermit,
        CoApplicantResidencePermitType:
          mainApplicantSchema.shape.ResidencePermitType,
        CoApplicantResidencePermitNumber:
          mainApplicantSchema.shape.ResidencePermitNumber,
        CoApplicantResidencePermitExpiration:
          mainApplicantSchema.shape.ResidencePermitExpiration,
        CoApplicantHasUnemploymentFund:
          mainApplicantSchema.shape.HasUnemploymentFund,
      }),
    [mainApplicantSchema.shape],
  );

  const schemaWithoutValidation = z
    .object({
      LoanDuration: z.number().nullable(),
      Purpose: z.nativeEnum(loan_application.Purpose).nullable(),
      OtherPurpose: z.string().nullable(),
      AppliedAmount: z.number().nullable(),
      ApplicantMode: z.nativeEnum(ApplicantMode),
      SelectedLenders: z.set(z.string().uuid()),
      CommentTitle: z.string().nullable(),
      CommentBody: z.string().nullable(),
      CommentCreatedBy: z.string().nullable(),
      CommentCreatedOn: z.string().nullable(),
    })
    .merge(mainApplicantSchema)
    .merge(coApplicantSchema);

  const validateApplication = useMemo(
    () =>
      debounceFunction(
        async (
          values: z.infer<typeof schemaWithoutValidation>,
          ctx: z.RefinementCtx,
        ) => {
          if (!applicationData) return; // Not point in validating without this data

          try {
            const result = await checkApplication({
              // Static application data
              ID: applicationID,
              CustomerID: applicationData?.CustomerID,
              CoApplicantCustomerID: applicationData?.CoApplicantCustomerID,
              PersonID: applicationData?.PersonID,
              CoApplicantPersonID: applicationData?.CoApplicantPersonID,
              MarketCountry: applicationData?.MarketCountry,
              Currency: applicationData?.Currency,
              Product: applicationData.Product,
              Meta: {
                Brand: applicationData.Meta?.Brand,
                Broker: applicationData.Meta?.Broker,
              },

              // Data provided by fields
              LoanDuration: values.LoanDuration,
              Purpose: values.Purpose,
              AppliedAmount: values.AppliedAmount,

              Citizenship: values.Citizenship,
              LivedInMarketCountrySince: values.LivedInMarketCountrySince,
              Education: values.Education,
              MilitaryServiceCompleted: values.MilitaryServiceCompleted,
              PoliticallyInfluential: values.PoliticallyInfluential,
              EmploymentStatus: values.EmploymentStatus,
              Employer: values.Employer,
              EmployerIndustry: values.EmployerIndustry,
              EmploymentStatusSince: values.EmploymentStatusSince,
              EmploymentStatusUntil: values.EmploymentStatusUntil,
              JobTitle: values.JobTitle,
              JobPosition: values.JobPosition,
              GrossIncomeMonthly: values.GrossIncomeMonthly,
              GrossIncomeYearly: values.GrossIncomeYearly,
              NetIncomeMonthly: values.NetIncomeMonthly,
              ExtraIncomeMonthly: values.ExtraIncomeMonthly,
              ExtraIncomeRental: values.ExtraIncomeRental,
              ExtraIncomeChildSupport: values.ExtraIncomeChildSupport,
              LivingCostMonthly: values.LivingCostMonthly,
              MaritalStatus: values.MaritalStatus,
              PartnerGrossIncomeMonthly: values.PartnerGrossIncomeMonthly,
              PartnerGrossIncomeYearly: values.PartnerGrossIncomeYearly,
              PartnerNetIncomeYearly: values.PartnerNetIncomeYearly,
              PartnerLivingCostMonthly: values.PartnerLivingCostMonthly,
              HousingType: values.HousingType,
              ApartmentType: values.ApartmentType,
              RentCostMonthly: values.RentCostMonthly,
              OwnerAssociationCostMonthly: values.OwnerAssociationCostMonthly,
              // eslint-disable-next-line @eslint-react/no-children-map -- False positive
              Children: values.Children.map((child) => ({
                ID: (child.ID ?? undefined) as never,
                Age: child.Age ?? 0,
                LoanApplicationID: applicationID,
              })),
              Debts: values.Debts.filter((debt) => Boolean(debt.Type)).map(
                (debt) => ({
                  ID: undefined as never,
                  Type: debt.Type as never,
                  Creditor: debt.Creditor ?? undefined,
                  MonthlyDebtPayment: debt.MonthlyDebtPayment ?? undefined,
                  NominalInterestRate: debt.NominalInterestRate ?? undefined,
                  OutstandingAmount: debt.OutstandingAmount,
                  RefinanceAmount: debt.RefinanceAmount ?? undefined,
                  Source: debt.Source ?? undefined,
                  Debtor: Debtor.MainApplicant,
                  LoanApplicationID: applicationID,
                }),
              ),
              CoApplicantCitizenship: values.CoApplicantCitizenship,
              CoApplicantLivedInMarketCountrySince:
                values.CoApplicantLivedInMarketCountrySince,
              CoApplicantEducation: values.CoApplicantEducation,
              CoApplicantEmployer: values.CoApplicantEmployer,
              CoApplicantEmploymentStatus: values.CoApplicantEmploymentStatus,
              CoApplicantEmploymentStatusSince:
                values.CoApplicantEmploymentStatusSince,
              CoApplicantEmploymentStatusUntil:
                values.CoApplicantEmploymentStatusUntil,
              CoApplicantGrossIncomeYearly: values.CoApplicantGrossIncomeYearly,
              CoApplicantNetIncomeYearly: values.CoApplicantNetIncomeYearly,
              CoApplicantLivingCostMonthly: values.CoApplicantLivingCostMonthly,
              CoApplicantMaritalStatus: values.MaritalStatus,
              CoApplicantPartnerGrossIncomeYearly:
                values.CoApplicantPartnerGrossIncomeYearly,
              CoApplicantHousingType: values.CoApplicantHousingType,
              CoApplicantRentCostMonthly: values.CoApplicantRentCostMonthly,
              CoDebts: values.CoDebts.filter((debt) => Boolean(debt.Type)).map(
                (debt) => ({
                  ID: undefined as never,
                  Type: debt.Type as never,
                  Creditor: debt.Creditor ?? undefined,
                  MonthlyDebtPayment: debt.MonthlyDebtPayment ?? undefined,
                  NominalInterestRate: debt.NominalInterestRate ?? undefined,
                  OutstandingAmount: debt.OutstandingAmount,
                  RefinanceAmount: debt.RefinanceAmount ?? undefined,
                  Source: debt.Source ?? undefined,
                  Debtor: Debtor.CoApplicant,
                  LoanApplicationID: applicationID,
                }),
              ),
              ResidencePermit: values.ResidencePermit,
              ResidencePermitType: values.ResidencePermitType,
              ResidencePermitNumber: values.ResidencePermitNumber,
              ResidencePermitExpiration: values.ResidencePermitExpiration,
              CoApplicantResidencePermit: values.CoApplicantResidencePermit,
              CoApplicantResidencePermitType:
                values.CoApplicantResidencePermitType,
              CoApplicantResidencePermitNumber:
                values.CoApplicantResidencePermitNumber,
              CoApplicantResidencePermitExpiration:
                values.CoApplicantResidencePermitExpiration,
              CompanyOrganizationNumber: values.CompanyOrganizationNumber,
              CoApplicantCompanyOrganizationNumber:
                values.CoApplicantCompanyOrganizationNumber,
              HasUnemploymentFund: values.HasUnemploymentFund,
              CoApplicantHasUnemploymentFund:
                values.CoApplicantHasUnemploymentFund,
              YearsAsBankCustomer: values.YearsAsBankCustomer,
              HasDankort: values.HasDankort,
              HasOtherPaymentCards: values.HasOtherPaymentCards,
              HasCars: values.HasCars,
              OwnsHolidayHome: values.OwnsHolidayHome,
              CurrentYearDebtOutstandingAmount:
                values.CurrentYearDebtOutstandingAmount,
              Cars: values.HasCars
                ? [
                    {
                      ID: '',
                      LoanApplicationID: applicationID,
                      OwnershipType:
                        values.CarOwnerShipType as CarOwnerShipType,
                      TotalCostMonthly: values.CarTotalCostMonthly ?? 0,
                    },
                  ]
                : [],
              Expenses: [
                values.ExpensesPhoneInternetTV
                  ? {
                      ID: undefined as never,
                      Kind: ExpenseKind.TVPhoneInternet,
                      LoanApplicationID: applicationID,
                      Value: values.ExpensesPhoneInternetTV,
                    }
                  : null,
                values.ExpensesTransport
                  ? {
                      ID: undefined as never,
                      Kind: ExpenseKind.Transport,
                      LoanApplicationID: applicationID,
                      Value: values.ExpensesTransport,
                    }
                  : null,
                values.ExpensesElectricity
                  ? {
                      ID: undefined as never,
                      Kind: ExpenseKind.Electricity,
                      LoanApplicationID: applicationID,
                      Value: values.ExpensesElectricity,
                    }
                  : null,
                values.ExpensesWater
                  ? {
                      ID: undefined as never,
                      Kind: ExpenseKind.Water,
                      LoanApplicationID: applicationID,
                      Value: values.ExpensesWater,
                    }
                  : null,
                values.ExpensesHeating
                  ? {
                      ID: undefined as never,
                      Kind: ExpenseKind.Heating,
                      LoanApplicationID: applicationID,
                      Value: values.ExpensesHeating,
                    }
                  : null,
                values.ExpensesInsurances
                  ? {
                      ID: undefined as never,
                      Kind: ExpenseKind.Insurance,
                      LoanApplicationID: applicationID,
                      Value: values.ExpensesInsurances,
                    }
                  : null,
              ].filter((x) => x !== null),
            });

            if (!result.Bad) return;

            if (result.Message)
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: result.Message,
              });

            result.Errors?.forEach((error) =>
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: error.Field.split('.'),
                message: error.Message,
              }),
            );
          } catch (e) {
            console.error('Failed form submission:', e);
          }
        },
        500,
      ),
    [applicationData, applicationID, checkApplication],
  );

  return schemaWithoutValidation.superRefine(validateApplication);
}

export function useApplicationDataFormResolver(applicationID: string) {
  const schema = useApplicationDataFormSchema(applicationID);

  return useMemo(
    () => zodResolver(schema, { async: true }, { mode: 'async' }),
    [schema],
  );
}

export type ApplicationDataFormValues = z.infer<
  ReturnType<typeof useApplicationDataFormSchema>
>;

export type ApplicationDataFormFieldPath = FieldPath<ApplicationDataFormValues>;

export type DebtItem = ApplicationDataFormValues['Debts'];
